import React, { useState, useCallback } from 'react';
import { Form, Input, Button, Tooltip, Space } from 'antd';
import { InfoCircleOutlined, EyeOutlined, EyeInvisibleOutlined, KeyOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../../hooks/useTranslationWithNamespace';
import { showSuccessNotification, showErrorNotification } from '../../../utils/notifications';
import { getPrivateKeyByAddress } from '../../../api';
import { PrivateKeyFieldProps } from './WithdrawalModalTypes';

/**
 * Private key input field component with toggle visibility and fetch functionality
 */
const WithdrawalModalPrivateKeyField: React.FC<PrivateKeyFieldProps> = ({
  form,
  walletAddress
}) => {
  const { t } = useTranslationWithNamespace('wallet');
  const [privateKeyVisible, setPrivateKeyVisible] = useState(false);
  const [isLoadingPrivateKey, setIsLoadingPrivateKey] = useState(false);

  // Function to retrieve private key
  const handleGetPrivateKey = useCallback(async () => {
    if (!walletAddress) {
      showErrorNotification(
        t('withdrawal.error', 'Error'),
        t('withdrawal.noWalletAddress', 'No wallet address available')
      );
      return;
    }

    try {
      setIsLoadingPrivateKey(true);
      console.log('Retrieving private key for address:', walletAddress);

      const privateKey = await getPrivateKeyByAddress(walletAddress);
      
      if (privateKey) {
        form.setFieldsValue({ private_key: privateKey });
        
        showSuccessNotification(
          t('withdrawal.privateKeyRetrieved', 'Private Key Retrieved'),
          t('withdrawal.privateKeyRetrievedMessage', 'The private key has been retrieved successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal.privateKeyError', 'Private Key Error'),
          t('withdrawal.privateKeyNotFound', 'Private key not found')
        );
      }
    } catch (error) {
      console.error('Error retrieving private key:', error);
      showErrorNotification(
        t('withdrawal.privateKeyError', 'Private Key Error'),
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      setIsLoadingPrivateKey(false);
    }
  }, [form, t, walletAddress]);

  return (
    <Form.Item
      name="private_key"
      label={
        <span>
          {t('withdrawal.privateKey', 'Private Key (Optional)')}
          <Tooltip title={t('withdrawal.privateKeyTooltip', 'Private key is optional. If not provided, the system will use the private key stored in the database.')}>
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      }
    >
      <Space.Compact style={{ width: '100%' }}>
        <Input.Password
          placeholder={t('withdrawal.privateKeyPlaceholder', 'Enter wallet private key (optional)')}
          autoComplete="new-password"
          name="private_key_no_autofill"
          iconRender={visible => (
            <Button
              type="text"
              icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setPrivateKeyVisible(!privateKeyVisible)}
              style={{ border: 'none', padding: 0 }}
            />
          )}
          visibilityToggle={{ visible: privateKeyVisible, onVisibleChange: setPrivateKeyVisible }}
        />
        <Button
          type="default"
          icon={<KeyOutlined />}
          onClick={handleGetPrivateKey}
          loading={isLoadingPrivateKey}
        >
          {t('withdrawal.fetchKey', 'Fetch Key')}
        </Button>
      </Space.Compact>
    </Form.Item>
  );
};

export default WithdrawalModalPrivateKeyField;
